/** Turn-scoped display contract. References never grant access to source data. */
export type PresentationLayer = { kind: 'register' | 'experience'; id: string };
export type PresentationMediaRef = {
  kind: 'image' | 'video' | 'document' | 'link';
  ref_id: string;
  label: string;
};
export type PresentationEvidenceRef = {
  kind: 'register_row' | 'telemetry' | 'metric' | 'media' | 'attachment' | 'user_request' | 'imported';
  ref_id: string;
  label: string;
};
export type PresentationEvidenceCard = {
  target_id: string;
  label: string;
  layer_id: string;
  observed_at: string;
  facts: { key: string; label: string; value: string }[];
  evidence_refs: PresentationEvidenceRef[];
  media_refs: PresentationMediaRef[];
};
export type PresentationTextSection = { title: string; body: string; target_ids: string[] };
export type PresentationAction = {
  kind: 'show' | 'compare' | 'explain' | 'resume';
  target_ids: string[];
  visible_layers: PresentationLayer[];
  camera: 'fit_targets' | 'preserve' | 'restore_checkpoint';
  selection: 'replace' | 'preserve';
  scene_id?: string;
  resume_checkpoint_id?: string;
};
export type PresentationUnavailableReason =
  | 'missing_view_context'
  | 'stale_evidence_context'
  | 'stale_story_context'
  | 'stale_scene_context'
  | 'stale_layer_context'
  | 'invalid_presentation_proposal'
  | 'no_presentation_proposal'
  | 'turn_failed';
export type BrainTeamPresentationReady = {
  schema_version: 1;
  status: 'ready';
  plan_id: string;
  turn_id: string;
  request_id: string;
  base_view_revision: number;
  base_interaction_revision: number;
  approved_story?: { story_id: string; revision: number };
  dataset_version?: string;
  action: PresentationAction;
  evidence_cards: PresentationEvidenceCard[];
  media_refs: (PresentationMediaRef & { target_id: string })[];
  text_sections: PresentationTextSection[];
};
export type BrainTeamPresentationUnavailable = {
  schema_version: 1;
  status: 'unavailable';
  plan_id: string;
  turn_id: string;
  request_id: string | null;
  base_view_revision: number | null;
  base_interaction_revision: number | null;
  reason: PresentationUnavailableReason;
};
export type BrainTeamPresentationPlan = BrainTeamPresentationReady | BrainTeamPresentationUnavailable;

const requireValue = (condition: boolean) => {
  if (!condition) throw new Error('Invalid presentation plan');
};
const record = (value: unknown, allowed: string[]): Record<string, unknown> => {
  requireValue(!!value && typeof value === 'object' && !Array.isArray(value));
  const result = value as Record<string, unknown>;
  requireValue(Object.keys(result).every((key) => allowed.includes(key)));
  return result;
};
const text = (value: unknown, maximum: number, minimum = 0): string => {
  requireValue(typeof value === 'string');
  // Pydantic bounds Unicode characters rather than UTF-16 code units.
  let length = 0;
  for (const _character of value as string) requireValue(++length <= maximum);
  requireValue(length >= minimum);
  return value as string;
};
const id = (value: unknown): string => {
  const result = text(value, 192, 1);
  requireValue(result.trim() === result && /^\S(?:.*\S)?$/.test(result));
  return result;
};
const revision = (value: unknown, minimum = 0): number => {
  requireValue(
    typeof value === 'number' && Number.isInteger(value) && value >= minimum && value <= 1_000_000_000,
  );
  return value as number;
};
const choice = <T extends string>(value: unknown, options: readonly T[]): T => {
  requireValue(typeof value === 'string' && options.includes(value as T));
  return value as T;
};
const list = <T>(value: unknown, maximum: number, parse: (item: unknown) => T): T[] => {
  requireValue(Array.isArray(value) && value.length <= maximum);
  return Array.from(value as unknown[]).map(parse);
};
const optional = <T>(value: unknown, fallback: T): unknown => (value === undefined ? fallback : value);
const ids = (value: unknown, maximum: number): string[] => {
  const result = list(value, maximum, id);
  requireValue(new Set(result).size === result.length);
  return result;
};
const media = (value: unknown): PresentationMediaRef => {
  const item = record(value, ['kind', 'ref_id', 'label']);
  return {
    kind: choice(item.kind, ['image', 'video', 'document', 'link'] as const),
    ref_id: id(item.ref_id),
    label: text(optional(item.label, ''), 160),
  };
};
const evidence = (value: unknown): PresentationEvidenceRef => {
  const item = record(value, ['kind', 'ref_id', 'label']);
  return {
    kind: choice(item.kind, [
      'register_row',
      'telemetry',
      'metric',
      'media',
      'attachment',
      'user_request',
      'imported',
    ] as const),
    ref_id: id(item.ref_id),
    label: text(optional(item.label, ''), 160),
  };
};
const card = (value: unknown): PresentationEvidenceCard => {
  const item = record(value, [
    'target_id',
    'label',
    'layer_id',
    'observed_at',
    'facts',
    'evidence_refs',
    'media_refs',
  ]);
  return {
    target_id: id(item.target_id),
    label: text(item.label, 160, 1),
    layer_id: text(optional(item.layer_id, ''), 96),
    observed_at: text(optional(item.observed_at, ''), 64),
    facts: list(optional(item.facts, []), 12, (raw) => {
      const fact = record(raw, ['key', 'label', 'value']);
      return { key: text(fact.key, 96, 1), label: text(fact.label, 96, 1), value: text(fact.value, 240, 1) };
    }),
    evidence_refs: list(optional(item.evidence_refs, []), 12, evidence),
    media_refs: list(optional(item.media_refs, []), 8, media),
  };
};

/** Returns a detached, validated value. A null result means invalid, not absent.
 * Callers must retain presence when reading malformed stored values so clients
 * cannot revive weaker legacy directives. Live catalog authorization is separate. */
export const parseBrainTeamPresentationPlan = (value: unknown): BrainTeamPresentationPlan | null => {
  try {
    const commonKeys = [
      'schema_version',
      'status',
      'plan_id',
      'turn_id',
      'request_id',
      'base_view_revision',
      'base_interaction_revision',
    ];
    const raw = record(value, [
      ...commonKeys,
      'reason',
      'approved_story',
      'dataset_version',
      'action',
      'evidence_cards',
      'media_refs',
      'text_sections',
    ]);
    requireValue(raw.schema_version === 1);
    const common = { schema_version: 1 as const, plan_id: id(raw.plan_id), turn_id: id(raw.turn_id) };
    if (raw.status === 'unavailable') {
      record(raw, [...commonKeys, 'reason']);
      const emptyContext =
        raw.request_id === null && raw.base_view_revision === null && raw.base_interaction_revision === null;
      return {
        ...common,
        status: 'unavailable',
        request_id: emptyContext ? null : id(raw.request_id),
        base_view_revision: emptyContext ? null : revision(raw.base_view_revision),
        base_interaction_revision: emptyContext ? null : revision(raw.base_interaction_revision),
        reason: choice(raw.reason, [
          'missing_view_context',
          'stale_evidence_context',
          'stale_story_context',
          'stale_scene_context',
          'stale_layer_context',
          'invalid_presentation_proposal',
          'no_presentation_proposal',
          'turn_failed',
        ] as const),
      };
    }
    requireValue(raw.status === 'ready');
    record(raw, [
      ...commonKeys,
      'approved_story',
      'dataset_version',
      'action',
      'evidence_cards',
      'media_refs',
      'text_sections',
    ]);
    const actionRaw = record(raw.action, [
      'kind',
      'target_ids',
      'visible_layers',
      'camera',
      'selection',
      'scene_id',
      'resume_checkpoint_id',
    ]);
    const action: PresentationAction = {
      kind: choice(actionRaw.kind, ['show', 'compare', 'explain', 'resume'] as const),
      target_ids: ids(actionRaw.target_ids, 24),
      visible_layers: list(actionRaw.visible_layers, 8, (value) => {
        const layer = record(value, ['kind', 'id']);
        return { kind: choice(layer.kind, ['register', 'experience'] as const), id: id(layer.id) };
      }),
      camera: choice(actionRaw.camera, ['fit_targets', 'preserve', 'restore_checkpoint'] as const),
      selection: choice(actionRaw.selection, ['replace', 'preserve'] as const),
      ...('scene_id' in actionRaw ? { scene_id: id(actionRaw.scene_id) } : {}),
      ...('resume_checkpoint_id' in actionRaw
        ? { resume_checkpoint_id: id(actionRaw.resume_checkpoint_id) }
        : {}),
    };
    requireValue(
      new Set(action.visible_layers.map((layer) => `${layer.kind}:${layer.id}`)).size ===
        action.visible_layers.length,
    );
    const result: BrainTeamPresentationReady = {
      ...common,
      status: 'ready',
      request_id: id(raw.request_id),
      base_view_revision: revision(raw.base_view_revision),
      base_interaction_revision: revision(raw.base_interaction_revision),
      action,
      evidence_cards: list(raw.evidence_cards, 24, card),
      media_refs: list(raw.media_refs, 8, (value) => {
        const item = record(value, ['kind', 'ref_id', 'label', 'target_id']);
        return {
          ...media({ kind: item.kind, ref_id: item.ref_id, label: item.label }),
          target_id: id(item.target_id),
        };
      }),
      text_sections: list(raw.text_sections, 4, (value) => {
        const section = record(value, ['title', 'body', 'target_ids']);
        const parsed = {
          title: text(section.title, 120, 1),
          body: text(section.body, 1200, 1),
          target_ids: ids(section.target_ids, 24),
        };
        requireValue(!!parsed.title.trim() && !!parsed.body.trim() && parsed.target_ids.length > 0);
        return parsed;
      }),
    };
    if ('approved_story' in raw) {
      const story = record(raw.approved_story, ['story_id', 'revision']);
      const storyId = text(story.story_id, 24, 24);
      requireValue(/^[a-f0-9]{24}$/.test(storyId));
      result.approved_story = { story_id: storyId, revision: revision(story.revision, 1) };
    }
    if ('dataset_version' in raw) result.dataset_version = id(raw.dataset_version);
    const count = action.target_ids.length;
    requireValue(
      result.evidence_cards.length === count &&
        result.evidence_cards.every((item, index) => item.target_id === action.target_ids[index]),
    );
    requireValue(result.media_refs.every((item) => action.target_ids.includes(item.target_id)));
    requireValue(
      result.media_refs.every((item) =>
        result.evidence_cards
          .find((entry) => entry.target_id === item.target_id)
          ?.media_refs.some(
            (ref) => ref.kind === item.kind && ref.ref_id === item.ref_id && ref.label === item.label,
          ),
      ),
    );
    requireValue(
      result.text_sections.every((section) =>
        section.target_ids.every((target) => action.target_ids.includes(target)),
      ),
    );
    if (action.kind === 'resume') {
      requireValue(
        !!action.resume_checkpoint_id &&
          !count &&
          !action.visible_layers.length &&
          !action.scene_id &&
          !result.text_sections.length &&
          !result.media_refs.length &&
          action.camera === 'restore_checkpoint' &&
          action.selection === 'preserve',
      );
    } else {
      requireValue(!action.resume_checkpoint_id && action.camera !== 'restore_checkpoint' && count >= 1);
      if (action.kind === 'compare') requireValue(count === 2);
      if (action.kind === 'explain') {
        requireValue(
          action.camera === 'preserve' && action.selection === 'preserve' && !action.visible_layers.length,
        );
        if (action.scene_id) requireValue(!!result.approved_story);
      } else requireValue(action.selection === 'replace' && !action.scene_id);
    }
    return result;
  } catch {
    return null;
  }
};

export const isBrainTeamPresentationPlan = (value: unknown): value is BrainTeamPresentationPlan =>
  parseBrainTeamPresentationPlan(value) !== null;
